import { Box, Link, Typography, type SxProps, type Theme } from '@mui/material';
import { FAISCA_REPO_URL } from '@/config/brand';
import { duration, easing } from '@/theme/motion';
import { FaiscaMark } from './FaiscaMark';

/**
 * Credit to the Faísca template at the end of every page: centred, below a divider, "Powered by"
 * over the Faísca logo (raised like a floating action button, it lifts and turns a little on
 * hover) and the word "Faísca". The whole block links to the template, in a new tab.
 */
export function FaiscaCredito({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Box
      component="footer"
      sx={[
        {
          borderTop: 1,
          borderColor: 'divider',
          mt: 4,
          pt: 3,
          pb: 4,
          display: 'flex',
          justifyContent: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Link
        href={FAISCA_REPO_URL}
        target="_blank"
        rel="noopener noreferrer"
        underline="none"
        color="inherit"
        aria-label="Powered by Faísca (abre o template no GitHub)"
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          '&:hover .faisca-fab, &:focus-visible .faisca-fab': {
            transform: 'translateY(-10px) rotate(-8deg)',
            boxShadow: '0 14px 28px -10px rgba(20, 14, 38, 0.55)',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&:hover .faisca-fab, &:focus-visible .faisca-fab': { transform: 'translateY(-6px)' },
          },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}
        >
          Powered by
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <Box
            className="faisca-fab"
            sx={{
              display: 'inline-flex',
              borderRadius: '30%',
              transform: 'translateY(-6px)',
              boxShadow: '0 8px 18px -8px rgba(20, 14, 38, 0.45)',
              transition: `transform ${duration.medium2}ms ${easing.springFast}, box-shadow ${duration.medium1}ms ${easing.standard}`,
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <FaiscaMark size={32} />
          </Box>
          <Typography
            component="span"
            sx={{ fontWeight: 700, fontVariationSettings: "'ROND' 100" }}
          >
            Faísca
          </Typography>
        </Box>
      </Link>
    </Box>
  );
}
